import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  VERSAO_DO_BUNDLE,
  ehVersaoValida,
  versaoNova,
  podeRecarregarSozinha,
  temCampoPreenchido,
} from '@/lib/novaVersao';

/**
 * Leva a aba para a versão nova do ECO sem depender de Ctrl+R. A regra de
 * quando pode recarregar mora em @/lib/novaVersao; aqui ficam os ouvidos
 * (versão no servidor, digitação, uso, visibilidade) e o aviso.
 */

/** Pergunta ao servidor. É um arquivo de 40 bytes, servido pelo nginx. */
const CONFERIR_A_CADA_MS = 5 * 60_000;
/** Voltar à janela também pergunta, mas não mais que isto. */
const INTERVALO_MINIMO_MS = 60_000;
/** Com versão nova pendente, reavalia o momento de recarregar. */
const AVALIAR_A_CADA_MS = 30_000;

// Versão para a qual esta aba já recarregou. Se depois da recarga o bundle
// ainda não é ela (cache no caminho, deploy pela metade), não tenta de novo:
// fica só o aviso. Laço de recarregamento é pior que versão velha.
const CHAVE = '__recarga_versao__';

async function versaoServida(): Promise<string | null> {
  try {
    const resposta = await fetch(`/version.txt?t=${Date.now()}`, { cache: 'no-store' });
    if (!resposta.ok) return null;
    const texto = (await resposta.text()).trim();
    return ehVersaoValida(texto) ? texto : null;
  } catch {
    return null;
  }
}

/** `pedido` é o clique no aviso: aí recarrega sempre, mesmo já tendo tentado. */
function recarregarPara(versao: string, pedido = false) {
  if (!pedido) {
    try {
      if (sessionStorage.getItem(CHAVE) === versao) return;
      sessionStorage.setItem(CHAVE, versao);
    } catch {
      // Sem armazenamento não há como saber se já tentou: não arrisca o laço.
      return;
    }
  }
  window.location.reload();
}

export default function AtualizacaoAutomatica({ versaoDoBundle = VERSAO_DO_BUNDLE }: { versaoDoBundle?: string }) {
  const ativo = ehVersaoValida(versaoDoBundle);
  const [pendente, setPendente] = useState<string | null>(null);
  const [avisoFechado, setAvisoFechado] = useState(false);
  const { pathname } = useLocation();

  const editados = useRef(new Set<Element>());
  const ultimaAtividade = useRef(Date.now());
  const ocultaDesde = useRef<number | null>(null);

  // O que a regra precisa saber, registrado desde o início: a digitação e o
  // uso que importam aconteceram antes de a versão nova aparecer.
  useEffect(() => {
    if (!ativo) return;
    ocultaDesde.current = document.hidden ? Date.now() : null;
    const aoDigitar = (e: Event) => {
      if (e.target instanceof Element) editados.current.add(e.target);
    };
    const aoUsar = () => { ultimaAtividade.current = Date.now(); };
    const aoMudarVisibilidade = () => {
      ocultaDesde.current = document.hidden ? (ocultaDesde.current ?? Date.now()) : null;
    };
    const usos = ['pointerdown', 'keydown', 'wheel', 'mousemove', 'touchstart'] as const;
    document.addEventListener('input', aoDigitar, true);
    usos.forEach(ev => window.addEventListener(ev, aoUsar, { capture: true, passive: true }));
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    return () => {
      document.removeEventListener('input', aoDigitar, true);
      usos.forEach(ev => window.removeEventListener(ev, aoUsar, { capture: true }));
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, [ativo]);

  // Pergunta ao servidor de tempos em tempos e ao voltar para a janela. Não
  // pergunta ao abrir: a página acabou de vir dele.
  useEffect(() => {
    if (!ativo) return;
    let ultima = Date.now();
    const conferir = async () => {
      if (Date.now() - ultima < INTERVALO_MINIMO_MS) return;
      ultima = Date.now();
      const servida = await versaoServida();
      if (versaoNova(versaoDoBundle, servida)) setPendente(servida);
    };
    const aoVoltar = () => { if (!document.hidden) void conferir(); };
    const intervalo = setInterval(() => void conferir(), CONFERIR_A_CADA_MS);
    window.addEventListener('focus', aoVoltar);
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener('focus', aoVoltar);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [ativo, versaoDoBundle]);

  // Trocar de tela com versão nova pendente: a tela nova vem do servidor.
  // O primeiro valor é a tela em que a aba abriu, não uma troca.
  const telaAnterior = useRef(pathname);
  useEffect(() => {
    if (telaAnterior.current === pathname) return;
    telaAnterior.current = pathname;
    if (pendente) recarregarPara(pendente);
  }, [pathname, pendente]);

  // Sem troca de tela: janela escondida ou parada, e nada digitado na tela.
  useEffect(() => {
    if (!pendente) return;
    const avaliar = () => {
      for (const el of editados.current) if (!el.isConnected) editados.current.delete(el);
      const pode = podeRecarregarSozinha({
        agora: Date.now(),
        ocultaDesde: ocultaDesde.current,
        ultimaAtividade: ultimaAtividade.current,
        temCampoPreenchido: temCampoPreenchido(editados.current),
      });
      if (pode) recarregarPara(pendente);
    };
    avaliar();
    const intervalo = setInterval(avaliar, AVALIAR_A_CADA_MS);
    return () => clearInterval(intervalo);
  }, [pendente]);

  if (!pendente || avisoFechado) return null;

  return (
    <div
      role="status"
      className="fixed left-1/2 top-2 z-[400] flex -translate-x-1/2 items-center gap-2 rounded-full border bg-card py-1 pl-3 pr-1 text-sm text-foreground shadow-lg"
    >
      <RefreshCw className="h-4 w-4 shrink-0 text-primary-texto" />
      <span className="whitespace-nowrap">Saiu uma versão nova do ECO.</span>
      <Button size="sm" className="h-7 rounded-full px-3 text-xs" onClick={() => recarregarPara(pendente, true)}>
        Atualizar agora
      </Button>
      {/* Fechar só esconde o aviso: a troca segue acontecendo no próximo
          momento seguro. */}
      <button
        type="button"
        onClick={() => setAvisoFechado(true)}
        className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
        aria-label="Fechar aviso de versão nova"
        title="Fechar aviso"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
